import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  CheckIcon,
  EllipsisHorizontalIcon,
  GlobeAltIcon,
  KeyIcon,
  LockClosedIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  DataTable,
  DataTableHead,
  DataTableBody,
  DataTableFoot,
  DataTableRow,
  DataTableCell,
  TableHeader,
} from '../ui/DataTable';
import { formatRelativeTime } from '../../utils/dateUtils';
import { endpointDisplayName } from '../../utils/endpointLabel';
import { ServiceStopChip } from './ServiceStopChip';
import { refusalReason } from '../../utils/apiRefusal';
import { useServiceRowMeta } from './useServiceRowMeta';
import {
  ACCESS_MODE_LABELS,
  ACCESS_MODE_SENTENCES,
  serviceAccessMode,
} from './detail/serviceAccess';
import { DeleteServiceDialog } from './DeleteServiceDialog';
import { ServiceStopDialogs, type ServiceStopDialogKind } from './detail/ServiceStopDialogs';
import { useDeleteEndpoint } from '../../hooks/useEndpoints';
import { useFlag } from '../../hooks/useFeatureFlags';
import { useOptionalToast } from '../../contexts/ToastContext';
import type { AuthPolicyMode, Endpoint } from '../../types';

/**
 * Shared AI Services table (task 378, Mockup J) — used by the Dashboard and
 * reused by the AI Services list page (task 379).
 *
 * Columns are limited to data the APIs serve today: Service (description),
 * Endpoint (method + path), Requests · all time, Interface (saved badge),
 * Saved tests, Last updated. Per-row metadata is fetched inside the component
 * via `useServiceRowMeta` (one react-query query per source per row) and
 * degrades to an em dash while loading or on error. Rows navigate to
 * `/services/{id}`.
 *
 * One optional column rides on the `usageCost` prop (task 1010): Cost this
 * period, plus a reconciling footer. Only the Dashboard passes it — the AI
 * Services list page omits the prop and renders exactly as before.
 */

/**
 * Per-service cost for the current period (task 1010), supplied by the caller
 * rather than fetched here: the Dashboard already holds GET /accounts/usage, and
 * the column's Total has to equal the usage-cost tile beside it — two requests
 * made at two moments could not guarantee that.
 *
 * All figures are DOLLARS with the plan markup already applied, exactly as the
 * tile computes it; the table never sees raw micro-USD and never applies markup.
 * `totalDollars` undefined means "cannot compute" (markup unresolved, usage
 * unavailable, or an innovate-service build without `byEndpoint`) and the whole
 * column degrades to em dashes.
 *
 * Absent prop ⇒ no cost column at all, which is how the AI Services list page
 * keeps rendering exactly as it did.
 */
export interface ServicesTableUsageCost {
  /** Endpoint id → dollars this period. A listed id with no entry has spent $0.00. */
  byEndpointDollars: Record<string, number>;
  /** The account-wide period total — the same number as the Dashboard tile. */
  totalDollars: number | undefined;
  /** Total minus the listed rows: archived or unlisted services. Undefined when zero. */
  remainderDollars: number | undefined;
  /** e.g. "Aug"; appended to the header when present. */
  periodLabel: string | null;
}

interface ServicesTableProps {
  endpoints: Endpoint[];
  isLoading?: boolean;
  /** Rendered instead of the table when there are no endpoints (and not loading). */
  emptyState?: React.ReactNode;
  /** When present, the table grows a Cost this period column and a reconciling footer. */
  usageCost?: ServicesTableUsageCost;
}

const EM_DASH = '—';

const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Who may call each service, as one icon in the Service cell (task 973).
 *
 * An icon rather than a column: the list's job is to let an owner SEE that a
 * service is open at a glance, and a seventh column of one-word labels would
 * cost more width than the answer is worth. The tooltip and the screen-reader
 * text carry the full sentence.
 */
const ACCESS_MODE_ICONS: Record<AuthPolicyMode, typeof GlobeAltIcon> = {
  public: GlobeAltIcon,
  apiKey: KeyIcon,
  jwt: LockClosedIcon,
  custom: LockClosedIcon,
};

function ServiceAccessIndicator({ endpoint }: { endpoint: Endpoint }) {
  const mode = serviceAccessMode(endpoint);
  const Icon = ACCESS_MODE_ICONS[mode];
  return (
    <span
      data-testid={`services-row-access-${endpoint.id}`}
      data-mode={mode}
      title={ACCESS_MODE_SENTENCES[mode]}
      className={`inline-flex flex-shrink-0 items-center ${
        mode === 'public'
          ? 'text-amber-500 dark:text-amber-400'
          : 'text-gray-400 dark:text-gray-500'
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">{`Access: ${ACCESS_MODE_LABELS[mode]}. ${ACCESS_MODE_SENTENCES[mode]}`}</span>
    </span>
  );
}

function methodBadgeClass(method: string): string {
  switch (method) {
    case 'GET':
      return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    case 'POST':
      return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    case 'PUT':
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    case 'PATCH':
      return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
    case 'DELETE':
      return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  }
}

function ServiceRow({
  endpoint,
  usageCost,
}: {
  endpoint: Endpoint;
  usageCost?: ServicesTableUsageCost;
}) {
  const navigate = useNavigate();
  const { requestsTotal, interfaceSaved, savedTestsTotal } = useServiceRowMeta(endpoint.id);
  // Hidden from anyone the API would refuse — the affordance twin of the
  // backend's endpoints.manage gate (task 863).
  const canDelete = useFlag('innovate.fe.services.delete');
  // Stop and Start's lever (task 1216), the same one the detail page reads.
  const stopEnabled = useFlag('innovate.fe.services.stop');
  const deleteEndpoint = useDeleteEndpoint();
  // The table is mounted by the Dashboard as well as the AI Services page, and
  // one of its suites renders it without the app shell, so the toast is optional
  // (the `useOptionalToast` precedent) — the row disappearing is the outcome.
  const toast = useOptionalToast();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Stop / Start from the row menu (task 1191): the same confirmations the AI
  // Service page opens, so the reason, the Schedules note, the refetch and the
  // toasts match. Every row is either running or stopped, so the menu always
  // has at least one item; Delete alone stays behind its flag.
  const [stopDialog, setStopDialog] = useState<ServiceStopDialogKind | null>(null);

  const handleDelete = () => {
    deleteEndpoint.mutate(endpoint.id, {
      onSuccess: () => {
        setConfirmingDelete(false);
        toast?.success(
          `Deleted ${endpointDisplayName(endpoint)}`,
          'You can restore it from Settings → Archive.',
          { label: 'View Archive', href: '/settings/archive' }
        );
      },
      // What the User is told is what the service said (the task-858 rule),
      // never a guess at which of several causes it was.
      onError: (error) => toast?.error('Could not delete this AI Service', refusalReason(error)),
    });
  };

  return (
    <DataTableRow onClick={() => navigate(`/services/${endpoint.id}`)}>
      <DataTableCell>
        <div className="flex min-w-0 items-center gap-2">
          <ServiceAccessIndicator endpoint={endpoint} />
          <div
            className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1"
            data-testid={`services-row-name-${endpoint.id}`}
          >
            {endpointDisplayName(endpoint)}
          </div>
          {/* A warning, not a blocker (task 1164): the row still opens. */}
          <ServiceStopChip stop={endpoint.stop} />
        </div>
      </DataTableCell>
      <DataTableCell>
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${methodBadgeClass(endpoint.method)}`}
          >
            {endpoint.method}
          </span>
          <span className="text-sm font-mono text-gray-700 dark:text-gray-300 truncate">
            {endpoint.path}
          </span>
        </div>
      </DataTableCell>
      <DataTableCell className="whitespace-nowrap text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
        <span data-testid={`services-row-requests-${endpoint.id}`}>
          {requestsTotal !== undefined ? requestsTotal.toLocaleString() : EM_DASH}
        </span>
      </DataTableCell>
      {usageCost && (
        <DataTableCell className="whitespace-nowrap text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
          {/* $0.00 is a real answer — this service cost nothing this period. The
              em dash is reserved for "cannot compute", which is the whole
              column's state, not one row's. */}
          <span data-testid={`services-row-cost-${endpoint.id}`}>
            {usageCost.totalDollars !== undefined
              ? usdFormatter.format(usageCost.byEndpointDollars[endpoint.id] ?? 0)
              : EM_DASH}
          </span>
        </DataTableCell>
      )}
      <DataTableCell className="whitespace-nowrap">
        {interfaceSaved ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200"
            data-testid={`services-row-interface-${endpoint.id}`}
          >
            <CheckIcon className="h-3 w-3" />
            Saved
          </span>
        ) : (
          <span
            className="text-sm text-gray-400 dark:text-gray-500"
            data-testid={`services-row-interface-${endpoint.id}`}
          >
            {EM_DASH}
          </span>
        )}
      </DataTableCell>
      <DataTableCell className="whitespace-nowrap text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
        <span data-testid={`services-row-tests-${endpoint.id}`}>
          {savedTestsTotal !== undefined ? savedTestsTotal.toLocaleString() : EM_DASH}
        </span>
      </DataTableCell>
      <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        {formatRelativeTime(endpoint.updatedAt)}
      </DataTableCell>
      {/* Row actions: Stop or Start (task 1191), then Delete behind its flag.
          Every click in here has to stop propagating or the row's own
          navigate fires underneath the menu; the dialogs render inside this
          wrapper so their clicks stop here too. */}
      <DataTableCell className="w-12 whitespace-nowrap text-right">
        <div onClick={(event) => event.stopPropagation()}>
          {(stopEnabled || canDelete) && (
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton
                data-testid={`services-row-actions-${endpoint.id}`}
                aria-label={`Actions for ${endpointDisplayName(endpoint)}`}
                className="inline-flex items-center rounded-md p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
              >
                <EllipsisHorizontalIcon className="h-5 w-5" />
              </MenuButton>
              <MenuItems className="absolute right-0 z-50 mt-2 w-44 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800">
                {stopEnabled && (
                  <MenuItem>
                    {endpoint.stop ? (
                      <button
                        type="button"
                        data-testid="service-row-start"
                        onClick={() => setStopDialog('resume')}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-50 dark:data-[focus]:bg-gray-700"
                      >
                        <PlayIcon className="h-4 w-4" />
                        Start
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid="service-row-stop"
                        onClick={() => setStopDialog('stop')}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 data-[focus]:bg-gray-50 dark:data-[focus]:bg-gray-700"
                      >
                        <StopIcon className="h-4 w-4" />
                        Stop
                      </button>
                    )}
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem>
                    <button
                      type="button"
                      data-testid={`services-row-delete-${endpoint.id}`}
                      onClick={() => setConfirmingDelete(true)}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 data-[focus]:bg-gray-50 dark:data-[focus]:bg-gray-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </button>
                  </MenuItem>
                )}
              </MenuItems>
            </Menu>
          )}

          <ServiceStopDialogs
            endpoint={endpoint}
            open={stopDialog}
            onClose={() => setStopDialog(null)}
          />

          {confirmingDelete && (
            <DeleteServiceDialog
              endpoint={endpoint}
              onClose={() => setConfirmingDelete(false)}
              onConfirm={handleDelete}
              isDeleting={deleteEndpoint.isPending}
            />
          )}
        </div>
      </DataTableCell>
    </DataTableRow>
  );
}

export default function ServicesTable({
  endpoints,
  isLoading = false,
  emptyState,
  usageCost,
}: ServicesTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40" data-testid="services-table-loading">
        <div className="text-gray-500 dark:text-gray-400">Loading AI Services...</div>
      </div>
    );
  }

  if (endpoints.length === 0) {
    if (emptyState) return <>{emptyState}</>;
    return (
      <div
        className="text-center py-12 text-sm text-gray-500 dark:text-gray-400"
        data-testid="services-table-empty"
      >
        No AI Services yet.
      </div>
    );
  }

  return (
    <div data-testid="services-table">
      <DataTable>
        <DataTableHead>
          <TableHeader>Service</TableHeader>
          <TableHeader>Endpoint</TableHeader>
          <TableHeader className="text-right">Requests · all time</TableHeader>
          {usageCost && (
            <TableHeader className="text-right">
              {usageCost.periodLabel
                ? `Cost this period · ${usageCost.periodLabel}`
                : 'Cost this period'}
            </TableHeader>
          )}
          <TableHeader>Interface</TableHeader>
          <TableHeader className="text-right">Scenarios</TableHeader>
          <TableHeader>Last updated</TableHeader>
          {/* The actions column is unlabelled — a header over a menu button
              would read as a seventh piece of data about the service. */}
          <TableHeader className="w-12">
            <span className="sr-only">Actions</span>
          </TableHeader>
        </DataTableHead>
        <DataTableBody>
          {endpoints.map((endpoint) => (
            <ServiceRow key={endpoint.id} endpoint={endpoint} usageCost={usageCost} />
          ))}
        </DataTableBody>
        {usageCost && (
          <DataTableFoot>
            {/* The listed rows need not reach the account total: the Dashboard
                lists at most ten services and an archived one keeps its usage
                rows. The remainder line is what makes the footer add up, so it
                is stated rather than left as a silent gap. */}
            {usageCost.remainderDollars !== undefined && (
              <DataTableRow>
                <DataTableCell className="text-sm text-gray-500 dark:text-gray-400" colSpan={3}>
                  Other AI Services (archived or not listed)
                </DataTableCell>
                <DataTableCell className="whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                  <span data-testid="services-table-cost-other">
                    {usdFormatter.format(usageCost.remainderDollars)}
                  </span>
                </DataTableCell>
                <DataTableCell colSpan={4}>{null}</DataTableCell>
              </DataTableRow>
            )}
            <DataTableRow>
              <DataTableCell
                className="text-sm font-semibold text-gray-900 dark:text-white"
                colSpan={3}
              >
                Total
              </DataTableCell>
              <DataTableCell className="whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                <span data-testid="services-table-cost-total">
                  {usageCost.totalDollars !== undefined
                    ? usdFormatter.format(usageCost.totalDollars)
                    : EM_DASH}
                </span>
              </DataTableCell>
              <DataTableCell colSpan={4}>{null}</DataTableCell>
            </DataTableRow>
          </DataTableFoot>
        )}
      </DataTable>
    </div>
  );
}
